import { type ButtonHTMLAttributes, forwardRef } from 'react';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

// UI_GUIDELINES §5 Button. Focus ring is global (index.css) — none here.
const variantClasses: Record<Variant, string> = {
  primary: 'bg-accent text-fg-on-accent font-semibold hover:bg-accent-hover active:bg-accent-pressed',
  secondary: 'border border-line-strong bg-surface-2 text-fg font-medium hover:bg-surface-3',
  danger: 'border border-danger/40 bg-danger-subtle text-danger font-medium hover:bg-danger/15',
  ghost: 'bg-transparent text-fg-secondary font-medium hover:bg-surface-2 hover:text-fg',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-4 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', isLoading, className = '', children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control transition-colors duration-fast disabled:cursor-not-allowed disabled:border-transparent disabled:bg-surface-2 disabled:text-fg-muted ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        {...props}
      >
        {isLoading && (
          <span
            aria-hidden="true"
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        )}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
